import { AppRoutes } from "@/routes/config";
import { clearOAuthBounceState, OAUTH_LOGIN_REASONS, recordOAuthBounce, reportOAuthBounceLoop, TOAuthLoginReason } from "@/utils/oauthTelemetry";
import { createUrlWithParams, getSafeRedirectUrl } from "@/utils/url";

/** irohana center origin, empty when the environment has no center configured. */
export const CENTER_URL = (process.env.REACT_APP_CENTER_URL ?? "").replace(/\/$/, "");

/** irohana center admin origin, empty when the environment has no admin center configured. */
export const ADMIN_CENTER_URL = (process.env.REACT_APP_ADMIN_CENTER_URL ?? "").replace(/\/$/, "");

/** Where the `product_contracts.id` of the last center hand-off is kept. */
const CENTER_PRODUCT_CONTRACT_ID_STORAGE_KEY = "match_center_product_contract_id";

/**
 * Bounces tolerated before the hand-off is treated as a loop. The center always
 * mints a token, so any session the product keeps rejecting (revoked token,
 * forbidden account state, mismatched query param) ping-pongs forever otherwise.
 */
const OAUTH_BOUNCE_LIMIT = 2;

/**
 * Records the `product_contracts.id` the center minted this session for (resolved from the
 * hand-off `contractToken`), so the next hand-off back to the center can name the contract it
 * wants a token for. The center re-checks it against the caller's own grants, so this is a
 * hint, not an authorization.
 *
 * Must be called AFTER the new session is persisted: the id is only resolvable through an
 * authenticated endpoint.
 */
export const rememberCenterProductContractId = (productContractId: number | string | null | undefined): void => {
  if (!productContractId) return;
  try {
    localStorage.setItem(CENTER_PRODUCT_CONTRACT_ID_STORAGE_KEY, String(productContractId));
  } catch {
    // Storage unavailable (private mode / quota) -> the next hand-off just omits the contract.
  }
};

/**
 * The id is a DB row id, so it cannot be hard-coded or configured — the only source is what the
 * center sent on the last hand-off. Undefined until then (a fresh browser landing straight on a
 * match URL), in which case the center resolves the contract from the redirectUrl origin.
 */
export const getCenterProductContractId = (): string | undefined => {
  try {
    return localStorage.getItem(CENTER_PRODUCT_CONTRACT_ID_STORAGE_KEY) || undefined;
  } catch {
    // Storage unavailable (private mode / quota) -> hand off without naming the contract.
    return undefined;
  }
};

/**
 * Absolute URL to return to after signing in at center. On `/login`, reuses the
 * `redirectUrl` query param to avoid a login loop; elsewhere, the current URL.
 * Absolute so center can resolve the contract from the origin.
 */
const getReturnUrl = (): string => {
  const path = location.pathname.startsWith(AppRoutes.login)
    ? getSafeRedirectUrl(new URLSearchParams(location.search).get("redirectUrl") ?? "")
    : `${location.pathname}${location.search}`;

  return new URL(path, location.origin).href;
};

/**
 * A bounce means one match -> center round-trip, and every round-trip is a fresh page
 * load. The redirect can be triggered more than once within the same load (an interval
 * tick racing a 401 handler), so record at most one bounce per load.
 */
let hasRecordedBounce = false;

/**
 * Hands authentication off to irohana center. The user signs in there and is
 * bounced back to `AppRoutes.authCallback` with a single-use token, which
 * `AuthCallbackPage` exchanges for a match session.
 *
 * No-ops when `REACT_APP_CENTER_URL` is unset so environments without center
 * configured do not navigate to a broken URL.
 */
export const initiateOAuthLogin = (reason: TOAuthLoginReason = OAUTH_LOGIN_REASONS.unauthenticated) => {
  if (!CENTER_URL) return;

  // Loop breaker: stop after a few round-trips in quick succession and report
  // the trail, instead of leaving the user on a page that flickers forever.
  if (!hasRecordedBounce) {
    hasRecordedBounce = true;
    const attempts = recordOAuthBounce(reason);
    if (attempts >= OAUTH_BOUNCE_LIMIT) {
      reportOAuthBounceLoop(reason, attempts);
      clearOAuthBounceState();
      location.replace(AppRoutes.serverErrorPage);
      return;
    }
  }

  window.location.href = createUrlWithParams(`${CENTER_URL}${AppRoutes.login}`, {
    productContractId: getCenterProductContractId(),
    redirectUrl: getReturnUrl(),
  });
};
